import { useEffect, useMemo } from 'react';
import { supportsGameGroups } from '@/config/games';
import { useGameStore } from '@/stores/game.store';
import { useGameGroupsStore, useUsableGameGroups } from '@/stores/gameGroups.store';

/** Groupe de jeu à interroger (`key` null : projet sans groupes, un seul satellite). */
export interface ProfilerGroup {
  key: string | null;
  label: string | null;
  color: string | null;
}

/**
 * Satellites à interroger pour le projet actif. L'espace Déploiement n'a pas de sélecteur de groupe :
 * la page lit donc chaque groupe que l'utilisateur peut utiliser (Nova, Iris), une requête par groupe,
 * et les réunit. Paladium n'a pas de groupes : une requête, sans en-tête de groupe.
 *
 * @returns `ready` faux tant que les groupes d'un jeu à groupes ne sont pas lus (rien n'est demandé
 *          avant, pour ne pas interroger le mauvais satellite)
 */
export function useProfilerGroups(): { gameId: string; groups: ProfilerGroup[]; ready: boolean } {
  const gameId = useGameStore((s) => s.activeGameId);
  const grouped = supportsGameGroups(gameId);
  const status = useGameGroupsStore((s) => s.status);
  const usable = useUsableGameGroups(gameId);

  useEffect(() => {
    if (grouped) void useGameGroupsStore.getState().fetch({ maxAgeMs: 60_000 });
  }, [grouped, gameId]);

  const groups = useMemo<ProfilerGroup[]>(() => {
    if (!grouped || usable.length === 0) return [{ key: null, label: null, color: null }];
    return usable.map((g) => ({ key: g.key, label: g.label, color: g.color ?? null }));
  }, [grouped, usable]);

  return { gameId, groups, ready: !grouped || status === 'loaded' || status === 'error' };
}
